"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE_PATH = "/image/new-version/machining/five-axis/materials";

// 金属材料数据
const metalMaterials = [
	{ key: "Aluminum", image: "aluminum.webp" },
	{ key: "Copper", image: "copper.webp" },
	{ key: "Brass", image: "brass.webp" },
	{ key: "Bronze", image: "bronze.webp" },
	{ key: "Steel", image: "steel.webp" },
	{ key: "StainlessSteel", image: "stainless-steel.webp" },
	{ key: "Magnesium", image: "magnesium.webp" },
	{ key: "Titanium", image: "titanium.webp" },
];

// 塑料材料数据
const plasticMaterials = [
	{ key: "ABS", image: "abs.webp" },
	{ key: "PC", image: "pc.webp" },
	{ key: "PMMA", image: "pmmaacrylic.webp" },
	{ key: "POM", image: "pom.webp" },
	{ key: "PA", image: "nylonpa.webp" },
	{ key: "PE", image: "pe.webp" },
	{ key: "PEEK", image: "peek.webp" },
	{ key: "PP", image: "pp.webp" },
	{ key: "HDPE", image: "hdpe.webp" },
	{ key: "HIPS", image: "hips.webp" },
	{ key: "LDPE", image: "ldpe.webp" },
	{ key: "PBT", image: "pbt.webp" },
	{ key: "PPA", image: "ppa.webp" },
	{ key: "PAI", image: "pai.webp" },
	{ key: "PET", image: "pet.webp" },
	{ key: "PPS", image: "pps.webp" },
	{ key: "PS", image: "ps.webp" },
	{ key: "PVC", image: "pvc.webp" },
	{ key: "PTFE", image: "ptfe.webp" },
	{ key: "UPE", image: "upe.webp" },
];

interface MaterialCardProps {
	materials: { key: string; image: string }[];
	type: "metal" | "plastic";
	title: string;
}

const MaterialCard = ({ materials, type, title }: MaterialCardProps) => {
	const t = useTranslations("fiveAxisMaterials");
	const [activeIndex, setActiveIndex] = useState(0);

	const activeMaterial = materials[activeIndex];
	const prefix = type === "metal" ? "metal" : "plastic";

	// 获取当前材料的名称、描述和合金/变体
	const materialName = t(`${prefix}${activeMaterial.key}` as any);
	const materialDesc = t(`${prefix}${activeMaterial.key}Desc` as any);
	const materialVariants = t(`${prefix}${activeMaterial.key}Variants` as any);

	return (
		<div className="flex flex-col">
			<h3 className="mb-6 ml-6 text-lg font-bold text-black sm:text-xl md:text-2xl">
				<span>{title}</span>
			</h3>
			{/* 左右布局：左侧Tab列表 + 右侧内容 */}
			<div className="flex flex-col gap-4 md:flex-row md:gap-0">
				{/* 左侧材料列表 - 垂直Tab按钮，可滚动 */}
				<div className="max-h-[650px] overflow-y-auto bg-[#f1f1f1] md:w-44">
					<div className="flex flex-row flex-wrap gap-1 md:flex-col md:gap-0">
						{materials.map((material, index) => (
							<button
								key={material.key}
								onClick={() => setActiveIndex(index)}
								className={`px-4 py-3 text-center text-sm font-medium transition-colors ${
									index === activeIndex
										? "bg-[#ededed] text-themePrimary400"
										: "text-gray-900 hover:text-themePrimary400"
								}`}
							>
								{t(`${prefix}${material.key}` as any)}
							</button>
						))}
					</div>
				</div>

				{/* 右侧内容区 - 图片和描述垂直排列 */}
				<div className="flex flex-1 flex-col gap-3 bg-white p-4">
					{/* 图片 */}
					<div className="relative aspect-[510/260] w-full overflow-hidden bg-white">
						<Image
							src={`${IMAGE_BASE_PATH}/${activeMaterial.image}`}
							alt={materialName}
							fill
							className="object-contain"
							sizes="(max-width: 768px) 100vw, 500px"
						/>
					</div>

					{/* 描述和变体 */}
					<div className="flex-1 bg-[#000000] p-5 text-white">
						<p className="mb-4 text-sm leading-relaxed opacity-70">{materialDesc}</p>
						<div>
							{type === "metal" && <p className="mb-2 text-sm opacity-70">{t("typicalBrand")}</p>}
							<div className="space-y-1">
								{materialVariants.split(",").map((variant, idx) => (
									<p key={idx} className="text-sm opacity-70">
										{variant.trim()}
									</p>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export interface AxisCncMachiningMaterialsProps {
	/** 自定义主标题，不传则使用多语言默认值 */
	title?: string;
	/** 自定义描述，不传则使用多语言默认值 */
	subtitle?: string;
	/** 自定义金属材料标题 */
	metalsTitle?: string;
	/** 自定义塑料材料标题 */
	plasticsTitle?: string;
}

const AxisCncMachiningMaterials = ({
	title,
	subtitle,
	metalsTitle,
	plasticsTitle,
}: AxisCncMachiningMaterialsProps) => {
	const t = useTranslations("fiveAxisMaterials");

	return (
		<section className="w-full bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 flex justify-between text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						<span>{title ?? t("title")}</span>
						<span className="text-[100px] font-bold text-themePrimary400">**</span>
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{subtitle ?? t("subtitle")}</p>
				</div>

				{/* 材料卡片区域 - 金属和塑料并排显示 */}
				<div className="mx-auto grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
					{/* 金属材料 */}
					<MaterialCard materials={metalMaterials} type="metal" title={metalsTitle ?? t("metalsTitle")} />

					{/* 塑料材料 */}
					<MaterialCard
						materials={plasticMaterials}
						type="plastic"
						title={plasticsTitle ?? t("plasticsTitle")}
					/>
				</div>
			</div>
		</section>
	);
};

export default AxisCncMachiningMaterials;
